/**
 * Zeiten und Spesen — was das Büro zur Lohnabrechnung braucht.
 *
 * ═══ HIER WIRD NICHT GERECHNET ═══
 *
 * Kein einziger Betrag entsteht in dieser Datei. Gerechnet wird ausschließlich
 * in der Datenbanksicht `spesentag`, gegen die datierte Satztabelle
 * `spesensatz`. Das ist keine Formsache: Ändert der Gesetzgeber die Sätze,
 * ändert sich eine Zeile in `spesensatz` — und alle Altfälle behalten ihren
 * damaligen Satz, weil die Sicht nach Datum zuordnet. Stünden die Zahlen auch
 * hier, gäbe es zwei Wahrheiten, und die zweite wäre still falsch.
 *
 * Dieselbe Rechnung läuft auf dem Handy des Fahrers als Vorschau mit. Auch dort
 * ist sie ausdrücklich nicht verbindlich. Verbindlich ist die Datenbank.
 *
 * ═══ WAS HIER PASSIERT: GRUPPIEREN ═══
 *
 * Die Tage werden zu Reisen gebündelt. Der Grund ist derselbe wie in der App:
 * 14 € · 28 € · 14 € an drei aufeinanderfolgenden Tagen ergibt nur zusammen
 * einen Sinn — hinfahren, ein voller Tag draußen, heimfahren. Einzeln gelesen
 * sieht der mittlere Tag nach einer Fehlbuchung aus.
 *
 * Die Regel ist wörtlich dieselbe wie in der Sicht: Ein Tag gehört zum Vortag,
 * wenn er von unterwegs kam — Zwischen- oder Abreisetag UND der Vortag war
 * wirklich der Kalendertag davor. Ein fehlender Tag dazwischen beendet die
 * Reise.
 */

import { supabase } from '../supabase';
import { aendern, anlegen, Speicherfehler } from './speichern';
import { alsKlarfehler } from './fehler';
import { heute } from './kalender';
import { bekommtBlock, monatDurchgehen, type MonatseintragVon } from './spesenmonat';

export { istReise } from './spesenmonat';

export type Spesenfall = 'eintaegig' | 'anreisetag' | 'zwischentag' | 'abreisetag';

export const FALLNAME: Record<Spesenfall, string> = {
  eintaegig: 'Eintägig',
  anreisetag: 'Anreisetag',
  zwischentag: 'Voller Tag unterwegs',
  abreisetag: 'Abreisetag',
};

export type Spesentag = {
  arbeitstag_id: string;
  mitarbeiter_id: string;
  datum: string;
  beginn: string | null;
  ende: string | null;
  dauer_minuten: number | null;
  pause_minuten: number | null;
  arbeitszeit_minuten: number | null;
  uebernachtung: boolean;
  zeit_fehlt: boolean;
  spesenfall: Spesenfall;
  verpflegung: number | null;
  uebernachtungsgeld: number | null;
  summe: number | null;
  /**
   * Der Stand des Arbeitstags. Jede Korrektur aus dem Büro schickt ihn mit —
   * sonst überschreibt sie stillschweigend, was der Fahrer am Monatsletzten
   * parallel eingetragen hat.
   */
  geaendert_am: string;
};

/**
 * Ein Eintrag in der Monatsliste — und warum die Lücke einer ist.
 *
 * ═══ WARUM DER MONAT VOLLSTÄNDIG SEIN MUSS ═══
 *
 * Vorher standen nur die Tage in der Liste, zu denen es einen Datensatz gab.
 * Ein vergessener Tag war deshalb nicht als Fehler zu sehen, sondern gar
 * nicht — zwischen dem 12. und dem 14. fehlte einfach eine Zeile, und niemand
 * zählt beim Lesen die Kalendertage mit. Genau so verschwindet
 * Verpflegungsmehraufwand: still, und dem Fahrer fällt es frühestens auf der
 * Lohnabrechnung auf.
 *
 * Jetzt hat jeder Kalendertag eine Zeile. Wo kein Datensatz ist, steht die
 * Lücke — mit einem Knopf, der sie schließt.
 */
export type Monatseintrag = MonatseintragVon<Spesentag>;

export type Mitarbeitermonat = {
  mitarbeiterId: string;
  name: string;
  tage: Spesentag[];
  /** Zusammenhängende Tage, aufsteigend — je Gruppe eine Reise oder ein Tag. */
  reisen: Spesentag[][];
  /**
   * Jeder Kalendertag des Monats bis heute, aufsteigend: Reise, einzelner Tag
   * oder Lücke. Das ist die Liste, die angezeigt wird.
   */
  eintraege: Monatseintrag[];
  verpflegung: number;
  uebernachtungsgeld: number;
  summe: number;
  naechte: number;
  ohneZeit: number;
  /**
   * Werktage (Mo–Fr) ohne jeden Datensatz.
   *
   * Bewusst nur Werktage: Ein Samstag ohne Eintrag ist der Normalfall und
   * würde die Zahl so aufblähen, dass sie niemand mehr liest. Feiertage zählen
   * mangels Kalender mit — genau die Unschärfe, die eine spätere Tagesart
   * (Urlaub, krank, Feiertag) beseitigen wird.
   */
  luecken: number;
};

export function euro(wert: number | null): string {
  if (wert === null) return '—';
  return wert.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

export function uhrzeit(wert: string | null): string {
  if (!wert) return '--:--';
  return new Date(wert).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function dauer(minuten: number | null): string {
  if (minuten === null) return '—';
  return `${Math.floor(minuten / 60)} h ${String(minuten % 60).padStart(2, '0')} min`;
}

export function tagText(datum: string): string {
  return new Date(datum + 'T12:00:00').toLocaleDateString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  });
}

export const MONATSNAMEN = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

function istVortag(datum: string, moeglicherVortag: string): boolean {
  const d = new Date(datum + 'T12:00:00');
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10) === moeglicherVortag;
}

/** Aufsteigend sortierte Tage in Reisen bündeln. Siehe Kopf dieser Datei. */
export function reisen(tage: Spesentag[]): Spesentag[][] {
  const aufsteigend = [...tage].sort((a, b) => a.datum.localeCompare(b.datum));
  const gruppen: Spesentag[][] = [];

  for (const tag of aufsteigend) {
    const laufende = gruppen[gruppen.length - 1];
    const vortag = laufende?.[laufende.length - 1];
    const gehoertDazu =
      !!vortag &&
      (tag.spesenfall === 'zwischentag' || tag.spesenfall === 'abreisetag') &&
      istVortag(tag.datum, vortag.datum);

    if (gehoertDazu) laufende.push(tag);
    else gruppen.push([tag]);
  }
  return gruppen;
}

export function reiseZeitraum(gruppe: Spesentag[]): string {
  if (gruppe.length === 1) return tagText(gruppe[0].datum);
  return `${tagText(gruppe[0].datum)} – ${tagText(gruppe[gruppe.length - 1].datum)}`;
}

/**
 * Einen Arbeitstag im Büro korrigieren.
 *
 * ═══ WARUM DAS BÜRO DAS ÜBERHAUPT DARF ═══
 *
 * Weil sonst niemand es dürfte. Der Fahrer korrigiert selbst, solange der
 * Monat läuft — danach sperrt ihn die Datenbank aus (`arbeitstag_frist_pruefen`).
 * Gäbe es hier keinen Weg, wäre eine im Oktober bemerkte Lücke aus dem
 * September für immer eine Lücke, und der Fahrer bekäme sein Geld nicht.
 *
 * ═══ WARUM JEDE ÄNDERUNG MITGESCHRIEBEN WIRD ═══
 *
 * Eine Arbeitszeit ist eine Lohngrundlage. Wer sie nachträglich ändert, ändert
 * einen Betrag, der schon ausgezahlt sein kann. Ohne Protokoll ist bei einer
 * Prüfung nicht zu belegen, ob korrigiert oder frisiert wurde — und die
 * Beweislast liegt beim Betrieb. Das Mitschreiben erledigt der Auslöser
 * `protokolliere_arbeitstag`; aus dieser Oberfläche kann niemand hineinschreiben
 * oder etwas wegräumen.
 */
/**
 * ═══ WARUM AUCH HIER DER GELESENE STAND MITGEHT ═══
 *
 * Das war die einzige Änderung im Backoffice, die an `aendern()` vorbeilief:
 * ohne `select()`, ohne Standprüfung. Beides hat gefehlt, und beides fehlte
 * ausgerechnet an einer Lohngrundlage.
 *
 * Ohne `select()` sieht niemand nach, ob überhaupt eine Zeile getroffen wurde
 * — greift die Zeilenregel, ändert Postgres nichts, PostgREST meldet keinen
 * Fehler, und der Bildschirm zeigt nach dem Auffrischen denselben Tag mit
 * denselben Zeiten. Es sieht aus, als hätte der Klick nicht gezählt.
 *
 * Ohne Standprüfung überschreibt das Büro am Monatsletzten die Korrektur, die
 * der Fahrer eine Minute vorher selbst eingetragen hat.
 */
export async function arbeitstagKorrigieren(
  tag: Spesentag,
  werte: { beginn?: string | null; ende?: string | null; uebernachtung?: boolean },
): Promise<void> {
  await aendern({
    tabelle: 'arbeitstag',
    id: tag.arbeitstag_id,
    gelesenerStand: tag.geaendert_am,
    werte,
    was: `Der Arbeitstag vom ${tagText(tag.datum)}`,
  });
}

/** Was am Arbeitstag geändert wurde, wann und von wem. */
export type Zeitaenderung = {
  id: string;
  feld: string;
  alt: string | null;
  neu: string | null;
  erstellt_am: string;
  /** Kennung des Bearbeiters — entscheidet über das Kennzeichen (HJ-030). */
  durch: string | null;
  durch_name: string | null;
};

export async function zeitaenderungenLaden(
  arbeitstagIds: string[],
): Promise<Map<string, Zeitaenderung[]>> {
  if (arbeitstagIds.length === 0) return new Map();

  const [ereignisse, leute] = await Promise.all([
    supabase
      .from('arbeitstag_ereignis')
      .select('*')
      .in('arbeitstag_id', arbeitstagIds)
      .order('erstellt_am', { ascending: false }),
    supabase.from('mitarbeiter').select('id, vorname, nachname'),
  ]);

  // Beide Abfragen prüfen: Schlüge die erste still fehl, stünde bei jedem Tag
  // „nie korrigiert" — und genau das ist die Auskunft, die man bei einer
  // Lohnprüfung glaubt.
  if (ereignisse.error) throw new Speicherfehler(alsKlarfehler(ereignisse.error));
  if (leute.error) throw new Speicherfehler(alsKlarfehler(leute.error));

  const name = new Map(
    (
      (leute.data ?? []) as { id: string; vorname: string; nachname: string }[]
    ).map((m) => [m.id, `${m.vorname} ${m.nachname}`]),
  );

  const nachTag = new Map<string, Zeitaenderung[]>();
  for (const roh of (ereignisse.data ?? []) as (Zeitaenderung & {
    arbeitstag_id: string;
    durch: string | null;
  })[]) {
    const liste = nachTag.get(roh.arbeitstag_id) ?? [];
    liste.push({
      id: roh.id,
      feld: roh.feld,
      alt: roh.alt,
      neu: roh.neu,
      erstellt_am: roh.erstellt_am,
      durch: roh.durch,
      durch_name: roh.durch ? (name.get(roh.durch) ?? null) : null,
    });
    nachTag.set(roh.arbeitstag_id, liste);
  }
  return nachTag;
}

/**
 * Einen Monat laden — je Mitarbeiter ein Block.
 *
 * Gelesen wird ein Tag VOR dem Monatsersten und einer danach, angezeigt wird
 * nur der Monat. Grund: Ob der 1. ein Abreisetag ist, entscheidet der 31. des
 * Vormonats; ob der Monatsletzte noch weiterläuft, entscheidet der 1. des
 * Folgemonats. Ohne diesen Rand wäre die Gruppierung an jedem Monatswechsel
 * einmal falsch — und zwar unauffällig falsch.
 */
export async function monatLaden(
  jahr: number,
  monatNr: number,
): Promise<Mitarbeitermonat[]> {
  const mm = String(monatNr).padStart(2, '0');
  const letzter = new Date(jahr, monatNr, 0).getDate();
  const monatsAnfang = `${jahr}-${mm}-01`;
  const monatsEnde = `${jahr}-${mm}-${letzter}`;
  // Die Lückenliste endet heute — ein Arbeitstag in der Zukunft lässt sich
  // nicht erfassen, und eine Reihe von Knöpfen, die nicht funktionieren können,
  // ist schlimmer als gar keine Zeile.
  const letzterTag = monatsEnde < heute() ? monatsEnde : heute();

  const vorlauf = new Date(monatsAnfang + 'T12:00:00');
  vorlauf.setDate(vorlauf.getDate() - 1);
  const nachlauf = new Date(monatsEnde + 'T12:00:00');
  nachlauf.setDate(nachlauf.getDate() + 1);

  const [tage, leute] = await Promise.all([
    supabase
      .from('spesentag')
      .select('*')
      .gte('datum', vorlauf.toISOString().slice(0, 10))
      .lte('datum', nachlauf.toISOString().slice(0, 10))
      .order('datum'),
    supabase
      .from('mitarbeiter')
      .select('id, vorname, nachname, status, rolle, auth_user_id, eintritt'),
  ]);

  if (tage.error) throw new Speicherfehler(alsKlarfehler(tage.error));
  // Auch die zweite Abfrage. Scheitert sie still, hätte jeder Block „Unbekannt"
  // als Namen — und niemand käme auf die Idee, dass eine Abfrage schiefging.
  if (leute.error) throw new Speicherfehler(alsKlarfehler(leute.error));

  type Person = {
    id: string;
    vorname: string;
    nachname: string;
    status: string;
    rolle: string;
    auth_user_id: string | null;
    eintritt: string | null;
  };
  const personen = (leute.data ?? []) as Person[];
  const nachKennung = new Map(personen.map((m) => [m.id, m]));

  const name = new Map(
    personen.map((m) => [m.id, `${m.vorname} ${m.nachname}`]),
  );

  const nachMitarbeiter = new Map<string, Spesentag[]>();
  for (const roh of (tage.data ?? []) as Spesentag[]) {
    const tag: Spesentag = {
      ...roh,
      // Nachgemessen: PostgREST liefert `numeric` als JSON-ZAHL, nicht als
      // Zeichenkette (`to_json(12.50::numeric)` ergibt `12.50` ohne
      // Anführungszeichen). Hier stand einmal das Gegenteil, und die Annahme
      // hat sich von hier aus weiterverbreitet.
      //
      // Die Umwandlung bleibt trotzdem: Sie kostet nichts, und sie fängt den
      // Fall ab, dass ein Wert über eine andere Schnittstelle als Text
      // hereinkommt. Was NICHT bleiben darf, ist der falsche Grund — sonst
      // baut jemand die nächste Entscheidung darauf.
      verpflegung: roh.verpflegung === null ? null : Number(roh.verpflegung),
      uebernachtungsgeld:
        roh.uebernachtungsgeld === null ? null : Number(roh.uebernachtungsgeld),
      summe: roh.summe === null ? null : Number(roh.summe),
    };
    const liste = nachMitarbeiter.get(tag.mitarbeiter_id) ?? [];
    liste.push(tag);
    nachMitarbeiter.set(tag.mitarbeiter_id, liste);
  }

  // ═══ WER ÜBERHAUPT EINEN BLOCK BEKOMMT ═══
  //
  // Früher nur, wer Tage hatte — damit fiel genau der Fall durch, um den es
  // geht: Ein Fahrer, der einen ganzen Monat nichts erfasst hat, war nicht als
  // Lücke zu sehen, sondern gar nicht da. Seit HJ-011 ist aber jeder Fahrer ab
  // dem Anlegen aktiv; deshalb zählt der leere Block nur für Fahrer mit
  // eingerichteter App. Wer im Monat Tage hat, bekommt immer einen —
  // Ausgeschiedene verschwinden nicht rückwirkend aus dem Juli. Die Regel
  // steht in `spesenmonat.ts` und wird von `npm run logik` geprüft.
  const imMonat = (t: Spesentag) => t.datum >= monatsAnfang && t.datum <= monatsEnde;
  const mitBlock = new Set<string>();
  for (const [mitarbeiterId, alle] of nachMitarbeiter) {
    if (alle.some(imMonat)) mitBlock.add(mitarbeiterId);
  }
  for (const person of personen) {
    if (bekommtBlock(person, mitBlock.has(person.id), monatsEnde)) mitBlock.add(person.id);
  }

  const bloecke: Mitarbeitermonat[] = [];

  for (const mitarbeiterId of mitBlock) {
    const alle = nachMitarbeiter.get(mitarbeiterId) ?? [];
    // Gruppiert wird über den ganzen gelesenen Zeitraum, angezeigt nur der
    // Monat — deshalb erst bündeln, dann beschneiden.
    const gruppen = reisen(alle)
      .map((gruppe) => gruppe.filter((t) => t.datum >= monatsAnfang && t.datum <= monatsEnde))
      .filter((gruppe) => gruppe.length > 0);

    const tageImMonat = alle.filter(imMonat);

    const eintraege = monatDurchgehen(
      tageImMonat,
      gruppen,
      monatsAnfang,
      letzterTag,
      nachKennung.get(mitarbeiterId)?.eintritt ?? null,
    );

    bloecke.push({
      mitarbeiterId,
      name: name.get(mitarbeiterId) ?? 'Unbekannt',
      tage: tageImMonat,
      reisen: gruppen,
      eintraege,
      verpflegung: tageImMonat.reduce((n, t) => n + (t.verpflegung ?? 0), 0),
      uebernachtungsgeld: tageImMonat.reduce((n, t) => n + (t.uebernachtungsgeld ?? 0), 0),
      summe: tageImMonat.reduce((n, t) => n + (t.summe ?? 0), 0),
      naechte: tageImMonat.filter((t) => t.uebernachtung).length,
      ohneZeit: tageImMonat.filter((t) => t.zeit_fehlt).length,
      luecken: eintraege.filter((e) => e.art === 'luecke' && e.werktag).length,
    });
  }

  return bloecke.sort((a, b) => a.name.localeCompare(b.name, 'de'));
}

/**
 * Einen fehlenden Arbeitstag anlegen.
 *
 * ═══ WARUM ER LEER ENTSTEHT ═══
 *
 * Ohne Zeiten. Der Tag ist danach ein „Zeit fehlt"-Tag wie jeder andere und
 * wird mit derselben Korrekturmaske gefüllt, die es ohnehin gibt. Ein zweites
 * Formular, das dasselbe schreibt, wäre eine zweite Stelle, an der die
 * Mitternachtsregel richtig sein muss.
 *
 * Ob es überhaupt erlaubt ist, entscheidet die Datenbank, nicht dieser Aufruf:
 * `arbeitstag_frist_pruefen` lässt den Fahrer nur im laufenden Monat und nie in
 * der Zukunft nachtragen, das Büro immer. Der Auslöser `protokolliere_arbeitstag`
 * schreibt „Tag nachgetragen" mit — eine rückwirkend erzeugte Lohngrundlage
 * muss als solche erkennbar bleiben.
 */
export async function arbeitstagNachtragen(
  mitarbeiterId: string,
  datum: string,
): Promise<void> {
  await anlegen('arbeitstag', {
    id: crypto.randomUUID(),
    mitarbeiter_id: mitarbeiterId,
    datum,
  });
}
